/**
 * Vite-specific exports for @order-up/client/vite
 *
 * Minimal export file containing only what Vite apps need.
 * This avoids bundling Next.js dependencies that cause issues in Vite.
 */

// Firebase initialization
export { getFirebaseAuth, getFirebaseFirestore, initialize } from './firebase/config';

// Firebase App Check (web clients)
export {
  enableAppCheckRefusalRecovery,
  getAppCheckHealth,
  getAppCheckToken,
  getFirebaseAppCheck,
  initializeFirebaseAppCheck,
  onAppCheckRefused,
} from './firebase/appCheck';
export type { AppCheckRefusal } from './firebase/appCheck';

// Selection context and hooks
export { SelectionProvider, useSelectedLocation, useSelectedTenant, useSelection } from './contexts/SelectionContext';

// Components needed by menu app
export { AuthenticatedRoute } from './components/AuthenticatedRoute';
export { CustomerAuthHandler } from './components/CustomerAuthHandler';
export { MenuDisplaySelector } from './components/MenuDisplaySelector';
export { MenuSelectionWizard } from './components/MenuSelectionWizard';
export { ClientProviders } from './providers/ClientProviders';

// Auth hooks
export * from './hooks/useAuth';
export { customerUidOf } from './utils/customerUid';
export * from './hooks/useAutoAnonymousAuth';

// User session sign-in mutation (mints the server session cookie from a
// Firebase ID token). Lives in useUserAuth, not useAuth. Exposed for the
// onboarding success screen, which re-mints the cookie with freshly-granted
// claims before handing off to management — see App.tsx AccountCreatedRedirect.
export { useUserSignIn } from './hooks/useUserAuth';

// Restaurant hooks
export * from './hooks/useAutoMenuSelection';
export * from './hooks/useLocations';

// Menu and item hooks
export { useGetItemsByRestaurantId } from './hooks/useItems';
export { useGetMenuGroupsByRestaurantId, useParentMenuGroups } from './hooks/useMenuGroups';

// Cart hooks and services
export * from './hooks/useCart';
export * from './hooks/useCartCalculations';
// Checkout must be able to retire the browser's copy of a cart once it becomes
// an order — the server stamps `convertedAt`, but a stale local cart would
// otherwise linger and be re-offered to the customer.
export { CartStorageService } from './services/cartStorage';
export * from './hooks/useOrderTimeAwareness';
export { cartService } from './services/cart';

// Order-config parity surface for the checkout app: the same scheduling +
// delivery-address controls the ordering app uses, so checkout can change the
// fulfillment time / order type in place instead of dead-ending on "go back".
export { default as ScheduleModal } from './components/ordering/ui/ScheduleModal';
// Publishes the restaurant's brand as `--menu-primary` for the ordering
// components above, which checkout also renders. Without this export the
// import type-checks against the source and dies only at bundle time.
export { MenuThemeScope } from './components/ordering/ui/MenuThemeScope';
export { default as DeliveryAddressDialog } from './components/ordering/ui/DeliveryAddressDialog';
export { isDeliveryAddressComplete, formatDeliveryAddress } from './components/ordering/ui/DeliveryAddressForm';
export { isASAPAvailable, formatASAPDisplay } from './components/ordering/schedulingUtils';

// Customer hooks and services
export * from './hooks/useCustomer';
export * from './hooks/usePhoneAuth';
export { customerService } from './services/customer';

// User hooks — Firestore `users/{uid}` doc accessor used by the onboarding
// wizard's `PersonalInfoStep` to seed first/last name from signup. Same
// hook the admin app uses, no app-specific copy.
export { useGetUserByUid } from './hooks/useUsers';

// Tax rate hooks
export * from './hooks/useTaxRates';

// Order hooks
export * from './hooks/useLiveOpenOrderRows';
export * from './hooks/useOrders';

// Checkout hooks and services
export * from './hooks/useCheckout';
export { checkoutService } from './services/checkout';
// Apple Pay merchant validation — through createApiClient so it reaches the API
// origin, never the page's own (the checkout SPA rewrite swallowed a relative path).

// Loyalty hooks and services (Customer Loyalty v1 — B8 checkout redemption UX).
// Vite-safe: useLoyalty only depends on createApiClient + React Query.
export * from './hooks/useLoyalty';
export { loyaltyService } from './services/loyalty';
export type { LoyaltyBalance } from './services/loyalty';

// Gift card tender at checkout (spec 2026-10-08 §4.2). Vite-safe: plain
// createApiClient calls + a script-tag Turnstile loader, no Next.js imports.
export * from './services/giftCardPublicApi';
export { TurnstileWidget } from './components/giftCards/TurnstileWidget';
export { guestProbeIds } from './components/giftCards/guestProbeIds';

// Utilities
export * from './utils/env';
export {
    formatTime,
    formatDate,
    formatDateTime,
    type TimeFormatSettings,
} from './utils/timeFormatter';
export {
    useTimeFormatter,
    type LocationTimeFormatter,
} from './hooks/useTimeFormatter';

// Boot recovery for a failed entry/vendor chunk fetch. Exported HERE as well as
// from the full barrel so a Vite app's entry never has to import
// `@order-up/client` (dist/index.js) just for this one function: that single
// import put the whole client barrel — Supabase, Books hooks, Next.js — into the
// guest checkout's first download (measured 2026-09-28: ~700 KB gzip).
export { installChunkReloadRecovery, type ChunkReloadRecoveryOptions } from './utils/chunkReloadRecovery';

// Logging utilities
export { ClientErrorHandler, createErrorHandler as createClientErrorHandler, errorHandler, getUserFriendlyMessage } from './utils/errorHandler';
export { ClientLogger, createLogger as createClientLogger, logger } from './utils/logger';

// Check hooks and services
export * from './hooks/useChecks';
export { checkService } from './services/checks';

// Payment hooks and services
export * from './hooks/usePaymentRecords';
export * from './hooks/usePayments';
export * from './hooks/useProcessPayment';
export * from './hooks/useSavedCards';
export { paymentService } from './services/payments';
export { paymentsApiService } from './services/paymentsApi';
export {
    initPayPageSession as initTrxPayPageSession,
    trxPaymentsApiService,
    type TrxPayPageInnerAction,
    type TrxPayPagePaymentType,
    type TrxPayPageSessionRequest,
    type TrxPayPageSessionResponse,
} from './services/trxPaymentsApi';

// Payment constants and utilities
export { ACH_RETURN_CODES, extractReceiptData, maskMerchantId } from './hooks/useProcessPayment';

// Re-export payment model types for convenience
export type {
    IAuthResponse, IEmvData, IReceiptData,
    IReceiptObjectData
} from '@order-up/models';

// Image components for optimized image loading (works with native img + srcset)
export { ResponsiveImage, useOptimalImageUrl } from './components/ResponsiveImage';

// Supabase (lazy initialization - auto-inits on first use)
export { getSupabaseClient, initializeSupabase, resetSupabaseClient } from './supabase/config';

// Onboarding hooks and services
export * from './hooks/useOnboarding';
export { getOnboardingService, OnboardingApplicationService, resetOnboardingService } from './services/onboarding';
export type { CreateApplicationInput, UpdateApplicationInput } from './services/onboarding';

// Business-types hook — used by `LocationDetailsStep` in the onboarding
// wizard to populate the concept dropdown from the admin-managed
// `business_types` collection. Same source the admin RestaurantModal uses,
// no drift between the "first restaurant" path and the "second location" path.
export * from './hooks/useBusinessTypes';

// Business sub-categories — exposed for the same LocationDetailsStep so
// it can render the second-level dropdown when the chosen business type
// has sub-categories (mirrors the admin RestaurantModal).
export * from './hooks/useBusinessSubCategories';

// Feature-flags provider (Phase 2 W A + Phase 6 W2). Browser-side
// implementation of `IFeatureFlags` from `@order-up/analytics`, backed by
// the Fastify per-caller evaluation endpoint. Exposed here so the Vite
// apps (menu, checkout, onboarding) can wire `<AnalyticsProvider>` from
// `@order-up/analytics` without pulling in the Next-flavored client
// barrel.
export {
    ClientApiFeatureFlagsProvider,
    clientApiFeatureFlagsProvider,
} from './providers/ClientApiFeatureFlagsProvider';

// Pay at table (Guest QR Platform piece 3, Task 8) — the three check-pay calls
// and the payer's balance poll, for `apps/checkout`'s `CheckPayPage`. Every one
// is UNAUTHENTICATED: the signed check token in the URL is the authorization,
// so they go through the Firebase-free public client and the page mounts
// OUTSIDE `<CustomerSession>` (Ruling R57). Without this export the page
// type-checks against the source and dies only at bundle time.
export {
    publicChecksApi,
    getGuestCheck,
    startCheckCardSession,
    payGuestCheck,
    isCheckMiss,
} from './services/publicChecks';
export type {
    CheckCardSession,
    CheckCardSessionOutcome,
    CheckPaymentInstrument,
    CheckWalletPayment,
    GuestCheckPayBody,
    GuestCheckPayResult,
} from './services/publicChecks';
export {
    guestCheckKeys,
    guestCheckQueryOptions,
    useGuestCheck,
    CHECK_POLL_MS,
} from './hooks/useGuestCheck';

// The narrowing every `ScheduleModal` / `OrderModal` consumer needs now that a
// cart can be `dineIn`. Exported rather than left in `components/ordering` so
// apps/checkout reaches the SAME rule instead of re-deriving a cast — a cast
// asserts the value cannot be dineIn, which is false.
export { toScheduledFulfilment } from './components/ordering/scheduledFulfilment';
export type { ScheduledFulfilment } from './components/ordering/scheduledFulfilment';

// Digital receipt / print view — `apps/checkout` `/receipt/:token`. Public: the
// signed print view token in the URL is the whole authorization.
export { publicPrintViewsApi, getPrintView, isPrintViewMiss } from './services/publicPrintViews';
export type { PrintView } from './services/publicPrintViews';
export { printViewKeys, usePrintView } from './hooks/usePrintView';
